import Parse from 'parse';

import { InjectionToken, Provider } from '@angular/core';
import {
  type AffiliateRepository,
  type CaseTypeRepository,
  type InvitationRepository,
  type OrganizationRepository,
  type RoleRepository,
  type UserRepository,
  type WorkspaceRepository,
} from '@telespot/domain';
import {
  ListDevicesHandler,
  ParseAffiliateRepository,
  ParseCaseRepository,
  ParseCaseTypeRepository,
  ParseDeviceRepository,
  ParseInvitationRepository,
  ParseOrganizationRepository,
  ParseRoleRepository,
  ParseUserRepository,
  ParseWorkspaceRepository,
} from '@telespot/infrastructure/parse';
import {
  AFFILIATE_REPOSITORY,
  ORG_STORE_DEVICE_REPOSITORY,
  CASE_REPOSITORY,
  ORGANIZATION_REPOSITORY,
  REGISTER_ORG_HANDLER,
  ORGANIZATION_REPOSITORY_LOADING_MODE,
  IS_NEW_ORGANIZATION,
  OrganizationStore,
} from '../stores/organization.store';
import {
  CountWorkspacesQuery,
  CREATE_WORKSPACE,
  CreateInvitationHandler,
  CreateWorkspaceHandler,
  DeleteWorkspaceHandler,
  type IWorkspaceMembershipRepository,
  ListUserMembershipsUseCase,
  ListWorkspaceMembersQuery,
  ListWorkspacesQuery,
  RegisterOrganizationHandler,
  RetrieveWorkspaceQuery,
  UpdateWorkspaceCommand,
} from '@telespot/management/use-cases';
import {
  ParseWorkspaceMembershipRepository,
} from '@management/infra/parse';
import { COUNT_WORKSPACES_QUERY, LIST_WORKSPACES_QUERY, WorkspacesEffects } from '../stores/workspaces.effects';
import { WorkspacesState, WORKSPACES_STATE_LOADING_MODE } from '../stores/workspaces.state';
import {
  RETRIEVE_WORKSPACE_QUERY,
  DELETE_WORKSPACE_HANDLER,
  WorkspaceDetailEffects,
} from '../stores/workspace-detail.effects';
import { WorkspaceDetailState } from '../stores/workspace-detail.state';
import { UPDATE_WORKSPACE_HANDLER, WorkspaceFormEffects } from '../stores/workspace-form.effects';
import { WorkspaceFormState } from '../stores/workspace-form.state';
import { PaginatedStateLoadingMode } from '@shared/ui';
import { DEVICE_PAGINATION_MODE, DeviceState } from '../stores/device.state';
import { DEVICE_REPOSITORY, DeviceEffects, LIST_DEVICES } from '../stores/device.effects';
import { CREATE_INVITATION_USE_CASE, INVITATION_REPOSITORY, InvitesStore } from '../stores/invites.store';
import { WorkspaceMembersState } from '../stores/user/workspace-members.state';
import {
  LIST_WORKSPACE_MEMBERS_QUERY,
  WORKSPACE_MEMBERS_CLIENT,
  WORKSPACE_MEMBERS_REPOSITORY,
  WORKSPACE_MEMBERSHIP_REPOSITORY,
  WorkspaceMembersEffects,
} from '../stores/user/workspace-members.effects';
import { UserMembershipsState } from '../stores/user/user-memberships.state';
import {
  USER_MEMBERSHIPS_CLIENT,
  USER_MEMBERSHIPS_LIST_USE_CASE,
  UserMembershipsEffects,
} from '../stores/user/user-memberships.effects';
import {
  LIST_USER_MEMBERSHIPS_USE_CASE,
  USER_REPOSITORY as AFFILIATED_USER_REPOSITORY,
  AFFILIATE_REPOSITORY as AFFILIATED_USER_AFFILIATE_REPOSITORY,
  USER_CLIENT,
  USERS_ROLES_REPOSITORY,
  USERS_WORKSPACE_REPOSITORY,
  AffiliatedUsersStore,
} from '../stores/user/affiliated-user.store';
import { UserClient } from '../stores/user/users.client';
import { Mediator } from '@telespot/shared/mediator';

const USER_REPOSITORY = new InjectionToken<UserRepository>('USER_REPOSITORY');

/**
 * Organization store providers.
 * This providers are used to provide the dependencies for the organization store and the organization related repositories.
 */
const ORGANIZATION_STORE_PROVIDERS: Provider[] = [
  {
    provide: ORGANIZATION_REPOSITORY,
    useFactory: () => new ParseOrganizationRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: AFFILIATE_REPOSITORY,
    useFactory: () => new ParseAffiliateRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: USER_REPOSITORY,
    useFactory: () => new ParseUserRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: CASE_REPOSITORY,
    useFactory: () => new ParseCaseRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: ORG_STORE_DEVICE_REPOSITORY,
    useFactory: () => new ParseDeviceRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: REGISTER_ORG_HANDLER,
    useFactory: (
      organizationRepository: OrganizationRepository,
      affiliateRepository: AffiliateRepository,
      userRepository: UserRepository
    ) => new RegisterOrganizationHandler(organizationRepository, affiliateRepository, userRepository),
    deps: [ORGANIZATION_REPOSITORY, AFFILIATE_REPOSITORY, USER_REPOSITORY],
  },
  {
    provide: IS_NEW_ORGANIZATION,
    useValue: false,
  },
  OrganizationStore,
];

/**
 * Organization store providers with loading mode.
 * This providers are used to provide the dependencies for the organization store and the organization related repositories.
 * It specifies the loading mode as a parameter.
 */
export function organizationStoreProviders(loadingMode: PaginatedStateLoadingMode): Provider[] {
  return [
    {
      provide: ORGANIZATION_REPOSITORY_LOADING_MODE,
      useValue: loadingMode,
    },
    ...ORGANIZATION_STORE_PROVIDERS,
  ];
}

const WORKSPACE_ORGANIZATION_REPOSITORY = new InjectionToken<OrganizationRepository>(
  'WORKSPACE_ORGANIZATION_REPOSITORY'
);
const WORKSPACE_AFFILIATE_REPOSITORY = new InjectionToken<AffiliateRepository>('WORKSPACE_AFFILIATE_REPOSITORY');
const WORKSPACE_CASE_TYPE_REPOSITORY = new InjectionToken<CaseTypeRepository>('WORKSPACE_CASE_TYPE_REPOSITORY');
export const WORKSPACE_REPOSITORY = new InjectionToken<WorkspaceRepository>('WORKSPACE_REPOSITORY');

/**
 * Aggregate workspaces providers — the new state/effects pairs for list, detail, and form.
 * Equivalent to composing workspacesListProviders + workspaceDetailProviders + workspaceFormProviders.
 */
export function workspaceStoreProviders(loadingMode: PaginatedStateLoadingMode): Provider[] {
  return [...workspacesListProviders(loadingMode), ...workspaceDetailProviders(), ...workspaceFormProviders()];
}

/**
 * Providers for the new WorkspaceFormState / WorkspaceFormEffects pair (workspace create/edit form).
 * Compose with workspaceDetailProviders since the form effects share the detail state and
 * RETRIEVE_WORKSPACE_QUERY for loading an existing workspace into the draft.
 */
export function workspaceFormProviders(): Provider[] {
  return [
    {
      provide: WORKSPACE_REPOSITORY,
      useFactory: () => new ParseWorkspaceRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_ORGANIZATION_REPOSITORY,
      useFactory: () => new ParseOrganizationRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_AFFILIATE_REPOSITORY,
      useFactory: () => new ParseAffiliateRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_CASE_TYPE_REPOSITORY,
      useFactory: () => new ParseCaseTypeRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: UPDATE_WORKSPACE_HANDLER,
      useFactory: (
        workspaceRepository: WorkspaceRepository,
        organizationRepository: OrganizationRepository,
        affiliateRepository: AffiliateRepository,
        caseTypeRepository: CaseTypeRepository
      ) =>
        new UpdateWorkspaceCommand(
          workspaceRepository,
          caseTypeRepository,
          new Mediator({
            provider: {
              [CREATE_WORKSPACE]: () =>
                new CreateWorkspaceHandler(
                  workspaceRepository,
                  organizationRepository,
                  affiliateRepository,
                  caseTypeRepository
                ),
            },
          })
        ),
      deps: [
        WORKSPACE_REPOSITORY,
        WORKSPACE_ORGANIZATION_REPOSITORY,
        WORKSPACE_AFFILIATE_REPOSITORY,
        WORKSPACE_CASE_TYPE_REPOSITORY,
      ],
    },
    WorkspaceFormState,
    WorkspaceFormEffects,
  ];
}

const DETAIL_CASE_TYPE_REPOSITORY = new InjectionToken<CaseTypeRepository>('DETAIL_CASE_TYPE_REPOSITORY');

/**
 * Providers for the new WorkspaceDetailState / WorkspaceDetailEffects pair (single workspace view).
 * Compose with workspacesListProviders so the detail effects can call WorkspacesState.removeItem
 * after a successful delete.
 *
 * NOTE: WorkspaceDetailState and WorkspaceDetailEffects are also visible to the workspace-cases
 * and workspace-members route children (they render inside the <router-outlet> of ts-workspace-detail
 * and inherit its DI scope). The cases/members facades inject WorkspaceDetailEffects from there to
 * trigger refreshWorkspace() after CRUD operations.
 */
export function workspaceDetailProviders(): Provider[] {
  return [
    {
      provide: WORKSPACE_REPOSITORY,
      useFactory: () => new ParseWorkspaceRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: DETAIL_CASE_TYPE_REPOSITORY,
      useFactory: () => new ParseCaseTypeRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: RETRIEVE_WORKSPACE_QUERY,
      useFactory: (workspaceRepository: WorkspaceRepository, caseTypeRepository: CaseTypeRepository) =>
        new RetrieveWorkspaceQuery(workspaceRepository, caseTypeRepository),
      deps: [WORKSPACE_REPOSITORY, DETAIL_CASE_TYPE_REPOSITORY],
    },
    {
      provide: DELETE_WORKSPACE_HANDLER,
      useFactory: (workspaceRepository: WorkspaceRepository) => new DeleteWorkspaceHandler(workspaceRepository),
      deps: [WORKSPACE_REPOSITORY],
    },
    WorkspaceDetailState,
    WorkspaceDetailEffects,
  ];
}

/**
 * Providers for the new WorkspacesState / WorkspacesEffects pair (paginated workspace list).
 * Composable; pair with workspaceDetailProviders / workspaceFormProviders as needed.
 */
export function workspacesListProviders(loadingMode: PaginatedStateLoadingMode): Provider[] {
  return [
    { provide: WORKSPACES_STATE_LOADING_MODE, useValue: loadingMode },
    {
      provide: WORKSPACE_REPOSITORY,
      useFactory: () => new ParseWorkspaceRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: COUNT_WORKSPACES_QUERY,
      useFactory: (workspaceRepository: WorkspaceRepository) => new CountWorkspacesQuery(workspaceRepository),
      deps: [WORKSPACE_REPOSITORY],
    },
    {
      provide: LIST_WORKSPACES_QUERY,
      useFactory: (workspaceRepository: WorkspaceRepository) => new ListWorkspacesQuery(workspaceRepository),
      deps: [WORKSPACE_REPOSITORY],
    },
    WorkspacesState,
    WorkspacesEffects,
  ];
}

/**
 * Device effects providers.
 * This providers are used to provide the dependencies for the device effects.
 */
const DEVICE_EFFECTS_PROVIDERS: Provider[] = [
  {
    provide: DEVICE_REPOSITORY,
    useFactory: () => new ParseDeviceRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: LIST_DEVICES,
    useFactory: () => new ListDevicesHandler(Parse, { useMasterKey: false }),
  },
  DeviceState,
  DeviceEffects,
];

/**
 * Generic device effects providers.
 * This providers are used to provide the dependencies for the device effects.
 * It specifies the loading mode as a parameter.
 */
export function deviceEffectsProviders(loadingMode: PaginatedStateLoadingMode): Provider[] {
  return [
    {
      provide: DEVICE_PAGINATION_MODE,
      useValue: loadingMode,
    },
    ...DEVICE_EFFECTS_PROVIDERS,
  ];
}

/**
 * Injection token for the roles repository that is needed by the invites store.
 */
const ROLES_REPOSITORY = new InjectionToken<RoleRepository>('ROLES_REPOSITORY');

/**
 * Invites store providers.
 * This providers are used to provide the dependencies for the invites store and the invites related repositories.
 */
const INVITES_STORE_PROVIDERS: Provider[] = [
  {
    provide: ROLES_REPOSITORY,
    useFactory: () => new ParseRoleRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: INVITATION_REPOSITORY,
    useFactory: () => new ParseInvitationRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: CREATE_INVITATION_USE_CASE,
    useFactory: (invitationRepository: InvitationRepository, rolesRepository: RoleRepository) =>
      new CreateInvitationHandler(invitationRepository, rolesRepository),
    deps: [INVITATION_REPOSITORY, ROLES_REPOSITORY],
  },
  InvitesStore,
];

/**
 * Generic invites store providers.
 * This providers are used to provide the dependencies for the invites store and the invites related repositories.
 */
export function invitesStoreProviders(): Provider[] {
  return [...INVITES_STORE_PROVIDERS];
}

/**
 * Workspace members store providers (paginated members list + memory + add/remove member operations).
 * Used by the workspace-members feature.
 */
export function workspaceMembersStoreProviders(): Provider[] {
  return [
    {
      provide: WORKSPACE_MEMBERS_REPOSITORY,
      useFactory: () => new ParseWorkspaceRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_MEMBERSHIP_REPOSITORY,
      useFactory: () => new ParseWorkspaceMembershipRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: LIST_WORKSPACE_MEMBERS_QUERY,
      useFactory: (repository: IWorkspaceMembershipRepository) => new ListWorkspaceMembersQuery(repository),
      deps: [WORKSPACE_MEMBERSHIP_REPOSITORY],
    },
    {
      provide: WORKSPACE_MEMBERS_CLIENT,
      useFactory: () => new UserClient(Parse),
    },
    WorkspaceMembersState,
    WorkspaceMembersEffects,
  ];
}

/**
 * User memberships store providers (active-user-being-edited flow).
 * Used by the user-creator and user-editor features.
 */
const USER_MEMBERSHIPS_WORKSPACE_REPOSITORY = new InjectionToken<WorkspaceRepository>(
  'USER_MEMBERSHIPS_WORKSPACE_REPOSITORY'
);
export function userMembershipsStoreProviders(): Provider[] {
  return [
    {
      provide: USER_MEMBERSHIPS_WORKSPACE_REPOSITORY,
      useFactory: () => new ParseWorkspaceRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: USER_MEMBERSHIPS_LIST_USE_CASE,
      useFactory: (workspaceRepository: WorkspaceRepository) => new ListUserMembershipsUseCase(workspaceRepository),
      deps: [USER_MEMBERSHIPS_WORKSPACE_REPOSITORY],
    },
    {
      provide: USER_MEMBERSHIPS_CLIENT,
      useFactory: () => new UserClient(Parse),
    },
    UserMembershipsState,
    UserMembershipsEffects,
  ];
}

/**
 * Affiliated user store providers.
 * This providers are used to provide the dependencies for the affiliated user store and the affiliated user related repositories.
 */
const AFFILIATED_USER_STORE_PROVIDERS: Provider[] = [
  {
    provide: AFFILIATED_USER_REPOSITORY,
    useFactory: () => new ParseUserRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: AFFILIATED_USER_AFFILIATE_REPOSITORY,
    useFactory: () => new ParseAffiliateRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: USER_CLIENT,
    useFactory: () => new UserClient(Parse),
  },
  {
    provide: USERS_WORKSPACE_REPOSITORY,
    useFactory: () => new ParseWorkspaceRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: USERS_ROLES_REPOSITORY,
    useFactory: () => new ParseRoleRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: LIST_USER_MEMBERSHIPS_USE_CASE,
    useFactory: (workspaceRepository: WorkspaceRepository) => new ListUserMembershipsUseCase(workspaceRepository),
    deps: [USERS_WORKSPACE_REPOSITORY],
  },
  AffiliatedUsersStore,
];

/**
 * Affiliated user store providers.
 * This providers are used to provide the dependencies for the affiliated user store and the affiliated user related repositories.
 */
export function affiliatedUserStoreProviders(): Provider[] {
  return [...AFFILIATED_USER_STORE_PROVIDERS];
}
